import { MagnifyingGlassIcon, FunnelIcon, EyeIcon, ArrowDownTrayIcon, DocumentIcon, DocumentTextIcon, PlusIcon, TrashIcon, BuildingOffice2Icon, CheckIcon } from "@heroicons/react/24/outline";
import Pagination from "components/common/Pagination";
import { Tooltip } from "components/common/Tooltip";
import { useTemplates } from "hooks/useTemplates";
import { useState, useEffect } from "react";
import { Template } from "types/template";
import { useLanguage } from "contexts/LanguageContext";
import DocumentPreviewModal from "components/preview/DocumentPreviewModal";
import CreateTemplateModal from "./CreateTemplateModal";
import ConfirmDeleteModal from "components/common/ConfirmDeleteModal";
import { useStorage } from "hooks/useStorage";
import toast from "react-hot-toast";
import { useOrganizations } from "hooks/useOrganizations";

interface TemplateListsProps {
  isCreateModalOpen: boolean;
  setIsCreateModalOpen: (isOpen: boolean) => void;
}

const TemplateLists = ({ isCreateModalOpen, setIsCreateModalOpen }: TemplateListsProps) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | "contract" | "quotation" | "other">("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { templates, loading, error, refreshTemplates, deleteTemplate, deleteTemplatesBulk } = useTemplates();
  const { deleteFile } = useStorage();
  const { organizations } = useOrganizations();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Dokumentum előnézet modal kezelése
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  
  // Tömeges kijelölés és törlés
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "" || template.type === typeFilter;

    return matchesSearch && matchesType;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTemplates = filteredTemplates.slice(indexOfFirstItem, indexOfLastItem);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getTypeIcon = (type: Template["type"]) => {
    switch (type) {
      case "contract":
        return <DocumentTextIcon className="h-5 w-5 text-primary-400" />;
      case "quotation":
        return <DocumentIcon className="h-5 w-5 text-amber-400" />;
      case "other":
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTypeName = (type: Template["type"]) => {
    switch (type) {
      case "contract":
        return t("templates.types.contract");
      case "quotation":
        return t("templates.types.quotation");
      case "other":
        return t("templates.types.other");
      default:
        return type;
    }
  };

  // Segédfüggvény a szervezet nevének megjelenítéséhez
  const getOrganizationName = (organizationId?: string) => {
    if (!organizationId) return "-";
    const org = organizations.find(org => org.id === organizationId);
    return org ? org.name : "-";
  };

  // Letöltés kezelése
  const handleDownload = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation();
    if (template.fileUrl) {
      const link = document.createElement('a');
      link.href = template.fileUrl;
      link.download = template.fileName || template.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Előnézet kezelése
  const handlePreview = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewTemplate(template);
    setIsPreviewOpen(true);
  };

  const handleDelete = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      try {
        // Töröljük a fájlt a storage-ból
        if (templateToDelete.fileUrl) {
          const filePath = templateToDelete.fileUrl.split('/o/')[1].split('?')[0];
          await deleteFile(decodeURIComponent(filePath));
        }

        // Töröljük a sablont az adatbázisból
        await deleteTemplate(templateToDelete.id);

        // Frissítjük a sablonok listáját
        await refreshTemplates();

        toast.success(t("templates.messages.deleted"));
      } catch (error) {
        console.error("Hiba a sablon törlése során:", error);
        toast.error(t("templates.messages.deleteError"));
      } finally {
        setIsDeleteModalOpen(false);
        setTemplateToDelete(null);
      }
    }
  };

  // Tömeges kijelölés kezelése
  const handleSelectTemplate = (templateId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTemplateIds(prev => [...prev, templateId]);
    } else {
      setSelectedTemplateIds(prev => prev.filter(id => id !== templateId));
    }
  };

  // Összes kijelölése/kijelölés törlése
  const handleSelectAllTemplates = (selectAll: boolean) => {
    setSelectAll(selectAll);
    if (selectAll) {
      setSelectedTemplateIds(currentTemplates.map(template => template.id));
    } else {
      setSelectedTemplateIds([]);
    }
  };

  // Tömeges törlés megerősítési ablak megnyitása
  const handleBulkDelete = () => {
    if (selectedTemplateIds.length > 0) {
      setIsBulkDeleteModalOpen(true);
    } else {
      toast.error(t("templates.messages.noTemplatesSelected"));
    }
  };

  // Tömeges törlés végrehajtása
  const handleConfirmBulkDelete = async () => {
    try {
      // Töröljük a fájlokat a storage-ból
      const selectedTemplates = templates.filter(template => selectedTemplateIds.includes(template.id));
      
      for (const template of selectedTemplates) {
        if (template.fileUrl) {
          try {
            const filePath = template.fileUrl.split('/o/')[1].split('?')[0];
            await deleteFile(decodeURIComponent(filePath));
          } catch (e) {
            console.warn(`Nem sikerült törölni a fájlt: ${template.fileUrl}`, e);
          }
        }
      }

      // Töröljük a sablonokat az adatbázisból
      await deleteTemplatesBulk(selectedTemplateIds);

      // Frissítjük a sablonok listáját
      await refreshTemplates();

      toast.success(t("templates.messages.bulkDeleted", { count: selectedTemplateIds.length }));
      
      // Töröljük a kijelöléseket
      setSelectedTemplateIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Hiba a sablonok tömeges törlése során:", error);
      toast.error(t("templates.messages.bulkDeleteError"));
    } finally {
      setIsBulkDeleteModalOpen(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        {/* Szűrők és keresés */}
        <div className="px-3 py-2 sm:py-2.5 lg:py-3 bg-gray-50 border-b border-gray-200 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 w-full">
            {/* Szűrők mobil nézetben egy gombra kattintással jelennek meg */}
            <div className="flex sm:hidden w-full">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder={t("templates.placeholder.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <button
                className="ml-2 bg-white border border-gray-300 p-1.5 rounded-md text-gray-600 hover:bg-gray-50"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Mobil nézetben lenyíló szűrők */}
            {isFilterOpen && (
              <div className="sm:hidden w-full mt-2">
                <select
                  className="w-full bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 mb-2"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as "" | "contract" | "quotation" | "other")}
                >
                  <option value="">{t("templates.filter.allTypes")}</option>
                  <option value="contract">{t("templates.filter.contract")}</option>
                  <option value="quotation">{t("templates.filter.quotation")}</option>
                  <option value="other">{t("templates.filter.other")}</option>
                </select>
              </div>
            )}

            {/* Asztali és tablet nézetű szűrők */}
            <div className="hidden sm:flex items-center space-x-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("templates.placeholder.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-44 lg:w-64 bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <select
                className="bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as "" | "contract" | "quotation" | "other")}
              >
                <option value="">{t("templates.filter.allTypes")}</option>
                <option value="contract">{t("templates.filter.contract")}</option>
                <option value="quotation">{t("templates.filter.quotation")}</option>
                <option value="other">{t("templates.filter.other")}</option>
              </select>
            </div>

            {/* Tömeges művelet gombok - csak ha van kiválasztva elem */}
            {selectedTemplateIds.length > 0 && (
              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">
                  {selectedTemplateIds.length} {t("templates.selected")}
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  {t("templates.actions.deleteSelected")}
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {/* ASZTALI ÉS TABLET NÉZET - TÁBLÁZAT */}
            <div className="hidden lg:block w-full">
              <div className="w-full overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-[5%]">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={selectAll}
                          onChange={(e) => handleSelectAllTemplates(e.target.checked)}
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-[25%]">{t("templates.fields.name")}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-[20%]">{t("templates.fields.description")}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-[20%]">{t("templates.fields.organization")}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-[10%]">{t("templates.fields.type")}</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-[20%]">{t("templates.fields.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentTemplates.map((template) => (
                      <tr key={template.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={selectedTemplateIds.includes(template.id)}
                            onChange={(e) => handleSelectTemplate(template.id, e.target.checked)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            <div className="p-1.5 rounded bg-gradient-to-br from-primary-500/20 to-primary-600/20 border-primary-500/20 border mr-2">
                              {getTypeIcon(template.type)}
                            </div>
                            <div>
                              <Tooltip content={template.name}>
                                <div className="text-sm font-medium text-gray-800 truncate max-w-[200px]">
                                  {template.name}
                                </div>
                              </Tooltip>
                              <div className="text-xs text-gray-600">
                                {getTypeName(template.type)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-gray-700">
                            {template.description}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            {template.organizationId && (
                              <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-500/10 text-primary-700 border border-primary-500/20">
                                <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                                {getOrganizationName(template.organizationId)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-gray-700">
                            {getTypeName(template.type)}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-center space-x-2">
                            <Tooltip content={t("templates.actions.preview")}>
                              <button
                                onClick={(e) => handlePreview(template, e)}
                                className="text-gray-500 hover:text-primary-600"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t("templates.actions.download")}>
                              <button
                                className="text-gray-500 hover:text-primary-600"
                                onClick={(e) => handleDownload(template, e)}
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t("templates.actions.delete")}>
                              <button
                                className="text-gray-500 hover:text-red-600"
                                onClick={(e) => handleDelete(template, e)}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* MOBIL NÉZET - KÁRTYÁK */}
            <div className="lg:hidden space-y-2 mb-2 w-full overflow-hidden">
              {currentTemplates.map((template) => (
                <div key={template.id} className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-gray-200 w-full overflow-hidden">
                  <div className="flex justify-between w-full">
                    <div className="flex items-center mr-2 mb-1.5 sm:mb-2 max-w-[60%]">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                        checked={selectedTemplateIds.includes(template.id)}
                        onChange={(e) => handleSelectTemplate(template.id, e.target.checked)}
                      />
                      <div className="p-1 sm:p-1.5 rounded bg-gradient-to-br from-primary-500/20 to-primary-600/20 border-primary-500/20 border mr-1 sm:mr-2 flex-shrink-0">
                        {getTypeIcon(template.type)}
                      </div>
                      <div className="min-w-0 w-full overflow-hidden">
                        <Tooltip content={template.name}>
                          <div className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                            {template.name}
                          </div>
                        </Tooltip>
                        <div className="text-[10px] sm:text-xs text-gray-600 truncate">
                          {getTypeName(template.type)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center flex-shrink-0 ml-auto space-x-0 sm:space-x-1">
                      <button
                        className="p-1 rounded-full text-gray-500 hover:text-primary-600 hover:bg-primary-500/10"
                        onClick={(e) => handlePreview(template, e)}
                      >
                        <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <button
                        className="p-1 rounded-full text-gray-500 hover:text-primary-600 hover:bg-primary-500/10"
                        onClick={(e) => handleDownload(template, e)}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <button
                        className="p-1 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-500/10"
                        onClick={(e) => handleDelete(template, e)}
                      >
                        <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1">
                    {template.organizationId && (
                      <div className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-primary-500/10 text-primary-700 border border-primary-500/20 max-w-[150px] overflow-hidden">
                        <BuildingOffice2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                        <span className="truncate">{getOrganizationName(template.organizationId)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
                    <div className="flex items-center text-gray-600">
                      <span className="font-medium text-gray-800 mr-1">{t("templates.fields.description")}:</span>
                      <span className="truncate">
                        {template.description}
                      </span>
                    </div>
                  </div>

                  <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
                    <div className="flex items-center text-gray-600">
                      <span className="font-medium text-gray-800 mr-1">{t("templates.fields.createdAt")}:</span>
                      <span>{template.createdAt?.toDate().toLocaleDateString("hu-HU")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredTemplates.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filteredTemplates.length}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}

            {filteredTemplates.length === 0 && (
              <div className="flex flex-col items-center justify-center p-8">
                <DocumentIcon className="h-12 w-12 text-gray-400 mb-3" />
                <p className="text-gray-600 text-sm">{t("templates.noTemplates")}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dokumentum előnézet modal */}
      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        template={previewTemplate}
      />

      {/* Új sablon létrehozás modal */}
      <CreateTemplateModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          refreshTemplates();
        }}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t("templates.deleteModal.title")}
        deleteMessage={t("templates.deleteModal.message", { name: templateToDelete?.name || "" })}
        deleteWarning={t("templates.deleteModal.warning")}
      />

      {/* Tömeges törlés megerősítő modal */}
      <ConfirmDeleteModal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        title={t("templates.bulkDeleteModal.title")}
        deleteMessage={t("templates.bulkDeleteModal.message", { count: selectedTemplateIds.length })}
        deleteWarning={t("templates.bulkDeleteModal.warning")}
      />
    </>
  );
};

export default TemplateLists;